/**
 * TemplateList component - List view of team and starter templates
 * Provides fork, duplicate, delete, and edit actions
 * Handles delete with project revert notification (FR-011)
 * Uses styled modals for fork name input and delete confirmation
 *
 * Phase 27: Unified templates - no media type badges/filters, 3 unified starters
 */

import {
  ChevronDown,
  ChevronRight,
  Copy,
  Edit3,
  GitFork,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ConfirmDeleteModal } from '@/components/ui/ConfirmDeleteModal';
import { TextInputModal } from '@/components/ui/TextInputModal';
import { useToast } from '@/components/ui/Toast';
import {
  useDeleteTemplate,
  useDuplicateTemplate,
  useForkTemplate,
  useTemplates,
} from '@/hooks/useTemplates';
import type { StarterTemplateInfo, Template } from '@/types/template';

// ============================================================================
// TYPES
// ============================================================================

export interface TemplateListProps {
  teamId: string;
  onEditTemplate: (templateId: string) => void;
  onCreateTemplate: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

function getUseCaseLabel(philosophy: string): string {
  const p = philosophy.toLowerCase();
  // Check conversion/direct response BEFORE brand — direct_response philosophy contains "brand building" text
  if (p.includes('conversion') || p.includes('direct response')) return 'Direct Response';
  if (p.includes('brand equity') || p.includes('long-term brand')) return 'Brand Building';
  if (p.includes('pass/fail') || p.includes('catch errors')) return 'Quality Check';
  return 'General';
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// ============================================================================
// STARTER CARD
// ============================================================================

interface StarterCardItemProps {
  starter: StarterTemplateInfo;
  onFork: (starter: StarterTemplateInfo) => void;
  isForking: boolean;
}

function StarterCardItem({ starter, onFork, isForking }: StarterCardItemProps) {
  const useCaseLabel = getUseCaseLabel(starter.philosophy ?? '');

  return (
    <Card className="flex flex-col justify-between">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-brand" />
          <h4 className="text-sm font-semibold text-primary">{useCaseLabel}</h4>
        </div>
        <p className="text-xs text-muted mb-2">{starter.philosophy ?? starter.description}</p>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">{starter.gateCount} gates</span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onFork(starter)}
          loading={isForking}
          leftIcon={isForking ? undefined : <GitFork className="h-3.5 w-3.5" />}
        >
          Fork
        </Button>
      </div>
    </Card>
  );
}

// ============================================================================
// TEMPLATE ROW
// ============================================================================

interface TemplateRowProps {
  template: Template;
  onEdit: (templateId: string) => void;
  onDuplicate: (templateId: string) => void;
  onDelete: (template: Template) => void;
  isDuplicating: boolean;
  isDeleting: boolean;
}

function TemplateRow({
  template,
  onEdit,
  onDuplicate,
  onDelete,
  isDuplicating,
  isDeleting,
}: TemplateRowProps) {
  const createdDate = new Date(template.createdAt).toLocaleDateString();

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg border border-border-subtle bg-surface hover:bg-surface-subtle transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-primary truncate">{template.name}</h4>
          {template.isDefault && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-brand/10 text-brand">
              Default
            </span>
          )}
        </div>
        {template.description && (
          <p className="text-xs text-muted mt-0.5 truncate">{template.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-muted">{template.gates.length} gates</span>
          <span className="text-xs text-muted">Created {createdDate}</span>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(template.templateId)}
          title="Edit template"
          className="!px-2"
        >
          <Edit3 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDuplicate(template.templateId)}
          loading={isDuplicating}
          title="Duplicate template"
          className="!px-2"
        >
          {!isDuplicating && <Copy className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(template)}
          loading={isDeleting}
          title="Delete template"
          className="!px-2 hover:text-danger"
        >
          {!isDeleting && <Trash2 className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TemplateList({
  teamId,
  onEditTemplate,
  onCreateTemplate,
}: TemplateListProps) {
  const { data, isLoading, isError } = useTemplates(teamId);
  const forkMutation = useForkTemplate(teamId);
  const duplicateMutation = useDuplicateTemplate(teamId);
  const deleteMutation = useDeleteTemplate(teamId);
  const toast = useToast();

  // Starters disclosure state (collapsed by default)
  const [startersExpanded, setStartersExpanded] = useState(false);

  // Track which item is being acted upon for loading state
  const [forkingKey, setForkingKey] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Modal state for fork name input
  const [forkModalOpen, setForkModalOpen] = useState(false);
  const [forkSourceStarter, setForkSourceStarter] = useState<StarterTemplateInfo | null>(null);

  // Modal state for delete confirmation
  const [deleteModalTemplate, setDeleteModalTemplate] = useState<Template | null>(null);

  const templates = data?.templates || [];
  const starters = data?.starters || [];

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleStarterFork = useCallback((starter: StarterTemplateInfo) => {
    setForkSourceStarter(starter);
    setForkModalOpen(true);
  }, []);

  const handleForkConfirm = useCallback(
    (name: string) => {
      if (!forkSourceStarter) return;
      setForkingKey(forkSourceStarter.key);
      setForkModalOpen(false);

      forkMutation.mutateAsync({ source: forkSourceStarter.key, name })
        .then((result) => {
          toast.success('Template forked', `Created "${name}" from starter.`);
          setForkingKey(null);
          setForkSourceStarter(null);
          if (result?.templateId) {
            onEditTemplate(result.templateId);
          }
        })
        .catch(() => {
          toast.error('Fork failed', 'Could not fork the template. Please try again.');
          setForkingKey(null);
          setForkSourceStarter(null);
        });
    },
    [forkSourceStarter, forkMutation, toast, onEditTemplate]
  );

  const handleForkModalClose = useCallback(() => {
    setForkModalOpen(false);
    setForkSourceStarter(null);
  }, []);

  const handleDuplicate = useCallback(
    (templateId: string) => {
      setDuplicatingId(templateId);
      duplicateMutation.mutate(templateId, {
        onSuccess: () => {
          toast.success('Template duplicated', 'A copy of the template has been created.');
          setDuplicatingId(null);
        },
        onError: () => {
          toast.error('Duplicate failed', 'Could not duplicate the template. Please try again.');
          setDuplicatingId(null);
        },
      });
    },
    [duplicateMutation, toast]
  );

  const handleDeleteClick = useCallback((template: Template) => {
    setDeleteModalTemplate(template);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteModalTemplate) return;
    setDeletingId(deleteModalTemplate.templateId);
    deleteMutation.mutate(deleteModalTemplate.templateId, {
      onSuccess: (response) => {
        if (response && response.revertedProjects > 0) {
          toast.info(
            'Template deleted',
            `${response.revertedProjects} project(s) reverted to the default template.`
          );
        } else {
          toast.success('Template deleted');
        }
        setDeletingId(null);
        setDeleteModalTemplate(null);
      },
      onError: () => {
        toast.error('Delete failed', 'Could not delete the template. Please try again.');
        setDeletingId(null);
        setDeleteModalTemplate(null);
      },
    });
  }, [deleteModalTemplate, deleteMutation, toast]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-brand animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-danger">Failed to load templates. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">Analysis Templates</h2>
          <p className="text-xs text-muted mt-1">
            Customize quality gates and evaluation criteria for your creative analyses.
          </p>
        </div>
        <Button
          onClick={() => onCreateTemplate()}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Create Template
        </Button>
      </div>

      {/* Starter Templates (collapsible) */}
      {starters.length > 0 && (
        <div>
          <button
            onClick={() => setStartersExpanded((prev) => !prev)}
            className="flex items-center gap-2 text-sm font-medium text-secondary cursor-pointer hover:text-primary transition-colors"
          >
            {startersExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Starter Templates ({starters.length} available)
          </button>
          {startersExpanded && (
            <div className="mt-3">
              <p className="text-xs text-muted mb-4">
                Fork a starter template as a starting point for your custom template.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {starters.map((starter) => (
                  <StarterCardItem
                    key={starter.key}
                    starter={starter}
                    onFork={handleStarterFork}
                    isForking={forkingKey === starter.key}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Team Templates */}
      <div>
        <h3 className="text-sm font-semibold text-secondary mb-3">Your Templates</h3>

        {templates.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-border-subtle rounded-lg">
            <p className="text-sm text-muted mb-2">
              No custom templates yet.
            </p>
            <p className="text-xs text-muted mb-4">
              Fork a starter template to begin, or create one from scratch.
            </p>
            <Button
              variant="secondary"
              onClick={() => onCreateTemplate()}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Create Template
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((template) => (
              <TemplateRow
                key={template.templateId}
                template={template}
                onEdit={onEditTemplate}
                onDuplicate={handleDuplicate}
                onDelete={handleDeleteClick}
                isDuplicating={duplicatingId === template.templateId}
                isDeleting={deletingId === template.templateId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fork Name Modal */}
      <TextInputModal
        isOpen={forkModalOpen}
        onClose={handleForkModalClose}
        onConfirm={handleForkConfirm}
        title="Fork Template"
        description="Create a custom copy of this starter template."
        label="Template Name"
        placeholder="e.g., My Custom Template"
        defaultValue={forkSourceStarter ? `${forkSourceStarter.name} (Custom)` : ''}
        confirmText="Fork"
        isLoading={!!forkingKey}
        required
        maxLength={100}
      />

      {/* Delete Template Modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteModalTemplate}
        onClose={() => setDeleteModalTemplate(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Template"
        description="This template will be permanently deleted. Any projects using it will revert to the default template."
        itemName={deleteModalTemplate?.name}
        confirmText="Delete"
        isLoading={!!deletingId}
      />
    </div>
  );
}
